import omit from "lodash.omit";
import { useNavigate, useOutletContext, useSearchParams } from "react-router-dom";
import CampaignDetailsForm, { sanitizeSensitiveFields, transformTargeting } from "../../components/forms/Campaign/CampaignDetailsForm";
import { useMutation } from "@apollo/client";
import { App as AntdApp } from "antd";
import { withAsyncErrorToast } from "../../utils/withToast";
import { CREATE_CAMPAIGN, UPDATE_CAMPAIGN } from "../../utils/api/campaign";
import { useCurrentUser } from "../../utils/CurrentUserContext";
import { CampaignType } from "../../__generated__/graphql";
import { validateCampaign } from "../../utils/validators";
import { trackEvent, timeEvent } from "../../utils/analytics";
import { useEffect } from "react";

const CreateCampaign = ({isNativeDisplay}: {isNativeDisplay: boolean}) => {
  const {user} = useCurrentUser();
  const { notification } = AntdApp.useApp();
  const {
    campaign, setCampaign,
    campaignDetails: [campaignDetails, setCampaignDetails],
    deal,
  } = useOutletContext<any>();

  const [createCampaign, { loading: saving }] = useMutation(CREATE_CAMPAIGN, {
    refetchQueries: ["CampaignMany"],
  });
  const [updateCampaign] = useMutation(UPDATE_CAMPAIGN);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Track campaign creation started
  useEffect(() => {
    if (!campaign) {
      trackEvent('campaign_create_started', {
        isNativeDisplay,
        companyType: user.company.type,
      });
      timeEvent('campaign_created');
    }
  }, [campaign, isNativeDisplay, user.company.type]);

  const handleNextClick = async (values: any) => {
    try {
      validateCampaign(values)
      setCampaignDetails(values);
      const { campaignPeriod, targeting, ...campaignDetails } = values;
      const sanitizedDetails = sanitizeSensitiveFields(campaignDetails, true);
      const normalizedDetails = sanitizedDetails.type === CampaignType.Filler
        ? omit(sanitizedDetails, ['targetValue', 'pacing'])
        : sanitizedDetails;

      const record = {
        ...transformTargeting(targeting),
        ...normalizedDetails,
        ...campaignPeriod,
        ...(deal?._id ? { dealId: deal._id } : {}),
      };

      if (campaign) {
        await withAsyncErrorToast(
          'Update campaign',
          () => updateCampaign({ variables: { id: campaign._id, record } }),
          undefined,
          notification
        );

        trackEvent('campaign_details_updated', {
          campaignId: campaign._id,
          campaignType: campaignDetails.type,
        });
      } else {
        const res = await withAsyncErrorToast(
          'Create campaign',
          () => createCampaign({ variables: { record } }),
          undefined,
          notification
        );

        if (res.data) {
          setCampaign(res.data.CampaignCreateOne);

          trackEvent('campaign_details_submitted', {
            campaignId: res.data.CampaignCreateOne._id,
            campaignType: campaignDetails.type,
            isNativeDisplay,
            step: 1,
            stepName: 'details',
            budget: campaignDetails.targetValue || null,
            startDate: campaignPeriod?.startDate || null,
            endDate: campaignPeriod?.endDate || null,
          });
        }
      }

      const params = searchParams.toString();
      navigate(`select-websites${params ? `?${params}` : ''}`);
    } catch (error) {
      trackEvent('validation_error', {
        formName: 'campaign_details',
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
      });
      throw error;
    }
  };

  // TODO: Temporary exemption for specific companies; remove when no longer needed
  const hidePriorityField = ["63c995f2656b6410511ae471"].includes(user?.company?._id);
  const hidePacingField = user?.company?._id === "657033321b972ed3447046ca";

  return (
    <CampaignDetailsForm
      defaultValues={campaignDetails}
      onSubmit={handleNextClick}
      submitButtonText="Next"
      isNewEntity={!campaign}
      accountType={user.company.type}
      isNativeDisplay={isNativeDisplay}
      hidePriorityField={hidePriorityField}
      hidePacingField={hidePacingField}
      isFromDeal={!!deal}
    />
  );
};

export default CreateCampaign;

